import React, { useState, useContext } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHeadset,
    faPaperPlane,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const AssignTicketToAgentList = ({
    show,
    onHide,
    ticket,
    currentUserId,
    currentUserName,
    onConfirm,
    inline = false
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!ticket) return null;

    const ticketId = ticket.idTicket || ticket.id;

    const handleConfirmAssignment = async () => {
        if (!currentUserId) return;

        setIsSubmitting(true);
        try {
            await onConfirm(ticket, {
                idUsuario: Number(currentUserId),
                vNombreCompleto: currentUserName || 'Asesor'
            });
            if (onHide) onHide();
        } finally {
            setIsSubmitting(false);
        }
    };

    if (inline) {
        return (
            <div className="d-flex flex-column gap-3 mb-3">
                {/* Tarjeta Principal: Tomar ticket y comenzar atención */}
                <div
                    className="p-3 rounded-3 border d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 shadow-none"
                    style={{
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
                        borderColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#bfdbfe'
                    }}
                >
                    <div className="d-flex align-items-start gap-3">
                        <div
                            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                                width: '46px',
                                height: '46px',
                                backgroundColor: '#2563eb',
                                color: '#ffffff',
                                fontSize: '1.25rem'
                            }}
                        >
                            <FontAwesomeIcon icon={faHeadset} />
                        </div>
                        <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                <h6 className="fw-bold mb-0" style={{ fontSize: '1.02rem', color: isDark ? '#f8fafc' : '#1e3a8a' }}>
                                    Tomar ticket y comenzar atención
                                </h6>
                                <span
                                    className="badge px-2 py-0.5"
                                    style={{
                                        fontSize: '0.68rem',
                                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe',
                                        color: isDark ? '#93c5fd' : '#1d4ed8',
                                        border: isDark ? '1px solid rgba(147, 197, 253, 0.3)' : '1px solid #bfdbfe'
                                    }}
                                >
                                    Requerido
                                </span>
                            </div>
                            <p className="mb-2 text-muted" style={{ fontSize: '0.90rem', maxWidth: '620px', lineHeight: 2 }}>
                                Este caso se encuentra pendiente de un asesor. Al tomar el ticket, te asignarás inmediatamente como responsable,
                                se notificará al cliente y se iniciará la medición de tu primer tiempo de respuesta.
                            </p>
                        </div>
                    </div>

                    {/* Botón de acción derecha */}
                    <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-md-auto">
                        <Button
                            variant="primary"
                            size="sm"
                            className="px-3 py-2 fw-semibold d-flex align-items-center gap-2 shadow-sm"
                            disabled={isSubmitting}
                            onClick={handleConfirmAssignment}
                            style={{ fontSize: '0.86rem', borderRadius: '8px' }}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    <span>Asignando...</span>
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faUserPlus} />
                                    <span>Asignarme este Ticket</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            backdrop="static"
            contentClassName={isDark ? 'bg-dark text-white border-secondary' : ''}
        >
            <Modal.Header closeButton className={isDark ? 'border-secondary' : ''}>
                <Modal.Title className="fs-2 d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faHeadset} className="text-primary" />
                    <span>Asignar Ticket #{ticketId}</span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-3">
                {/* Info del Ticket */}
                <div className={`p-2 rounded-2 mb-3 ${isDark ? 'bg-200 text-light' : 'bg-light'}`} style={{ fontSize: '0.85rem' }}>
                    <p className="mb-1"><strong>Cliente:</strong> {ticket.vCustomerName || ticket.customerName || 'Cliente WhatsApp'}</p>
                    <p className="mb-0"><strong>Teléfono:</strong> {ticket.vTelefono || ticket.phone || 'Sin teléfono'}</p>
                </div>

                {/* Mensaje de Confirmación / Explicación del flujo */}
                <div className="alert alert-info py-2 px-3 mb-0" style={{ fontSize: '0.82rem' }}>
                    <div className="d-flex align-items-start gap-2">
                        <FontAwesomeIcon icon={faPaperPlane} className="mt-1 text-info" />
                        <div>
                            Al confirmar, el ticket pasará a <strong>En Progreso</strong> y te asignarás como asesor responsable. Se enviará automáticamente el mensaje de WhatsApp al cliente con tu saludo personalizado.
                        </div>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className={`d-flex justify-content-end gap-2 ${isDark ? 'border-secondary' : ''}`}>
                <Button
                    variant="danger"
                    size="sm"
                    onClick={onHide}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 fw-semibold shadow-none"
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleConfirmAssignment}
                    disabled={isSubmitting}
                    className="px-3 py-1.5 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                >
                    {isSubmitting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span>Asignando...</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faUserPlus} />
                            <span>Asignarme a mí</span>
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignTicketToAgentList;
